// Active-run resume: the run id + token are persisted while a chain is in
// flight so a page refresh / tab close / crash doesn't lose them (the chain
// keeps running server-side either way). Cleared once a terminal state lands.
//
// The record is stamped with the access code it belongs to. A run from a
// DIFFERENT code (a prior code reused in the same browser) must NOT resume —
// otherwise a freshly-entered code lands on the setup screen with a stale
// run's completed steps already checked off. See loadActiveRunForCode.

export const ACTIVE_RUN_STORAGE_KEY = "aurora.cloud.activeRun";

export type ActiveRunPersist = {
  kind: "preview" | "convert";
  runId: string;
  runToken: string;
  startedAt: string;
  /** The access code this run was started under. */
  accessCode: string;
  /** True once the user confirmed "I've saved my passphrase" (the RunHandoff
   *  gate). Persisted so a mid-run reload doesn't re-run the gate ceremony
   *  for an ack that already happened (live friction, 2026-06-12). Optional:
   *  pre-existing records lack it and behave as un-acked (safe default). */
  passAcked?: boolean;
};

export function loadActiveRun(): ActiveRunPersist | null {
  try {
    const raw = localStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (
      p &&
      typeof p === "object" &&
      (p.kind === "preview" || p.kind === "convert") &&
      typeof p.runId === "string" &&
      typeof p.runToken === "string" &&
      typeof p.accessCode === "string"
    ) {
      return p as ActiveRunPersist;
    }
  } catch {
    // corrupt JSON — fall through and clear it below
  }
  // Invalid / legacy (pre-accessCode) record → clear so it can't resume.
  localStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
  return null;
}

/** Returns the persisted active run ONLY if it belongs to `code`. A run
 *  stamped with a different (or missing) access code is stale — clear it so a
 *  fresh code starts on a clean setup screen instead of resuming the old run
 *  and pre-checking its completed steps. */
export function loadActiveRunForCode(code: string): ActiveRunPersist | null {
  const active = loadActiveRun();
  if (!active) return null;
  if (active.accessCode !== code) {
    clearActiveRun();
    return null;
  }
  return active;
}

export function saveActiveRun(r: ActiveRunPersist): void {
  localStorage.setItem(ACTIVE_RUN_STORAGE_KEY, JSON.stringify(r));
}

/** Stamp the persisted record once the passphrase gate's ack commits, so a
 *  reload resumes past the gate. No-op when nothing is persisted. */
export function markActiveRunPassAcked(): void {
  const active = loadActiveRun();
  if (!active) return;
  saveActiveRun({ ...active, passAcked: true });
}

export function clearActiveRun(): void {
  localStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
}

// ---- resume dead-end notification -----------------------------------------
// Fired by App at the two places its resume probe concludes there is NOTHING
// to restore (an unknown/consumed state, or a 404/410 purge) — the moments a
// returning visitor can be left on an empty wizard holding a consumed code,
// with Landing unreachable (`welcomed` skips it) and no way to enter a new
// code (live on staging, 2026-08-06). Shell listens and re-runs its access-
// code revalidation, whose existing verdict logic then does the right thing:
// a still-valid code stays put; a dead one ejects to Landing, where the
// welcome-back / different-code / reopen machinery already works.
//
// Deliberately NOT fired from clearActiveRun() itself: cancel-and-retry and
// mid-session failure paths also clear the pointer, and ejecting there would
// destroy the in-memory corpus of a user who is still present (the exact
// regression class of audit B4 / 2026-06-11).
export const RESUME_DEAD_END_EVENT = "aurora:resume-dead-end";

export function notifyResumeDeadEnd(): void {
  try {
    window.dispatchEvent(new CustomEvent(RESUME_DEAD_END_EVENT));
  } catch {
    // No window (SSR/tests without jsdom) — nothing to notify.
  }
}
